import { api } from "@/lib/api";
import { cn, formatCurrency, formatDate } from "@/lib/utils";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Loader2, Plus } from "lucide-react";

// ============================================================
// Types
// ============================================================

interface VoucherStep {
	id: string;
	voucherNumber: string | null;
	voucherDate: string | null;
	voucherStatus: string;
	totalAmount: number | null;
	openAmount: number | null;
	lexofficeUrl: string;
}

interface PipelineDeal {
	groupKey: string;
	booking: {
		id: string;
		kunde: string;
		status: string;
		datum: string | null;
	} | null;
	contactName: string | null;
	steps: {
		quotation: VoucherStep | null;
		orderconfirmation: VoucherStep | null;
		invoice: VoucherStep | null;
	};
	totalAmount: number;
	latestDate: string | null;
}

// ============================================================
// Pipeline Section
// ============================================================

export function PipelineSection() {
	const pipelineQuery = useQuery({
		queryKey: ["lexoffice", "pipeline"],
		queryFn: () => api.get<{ data: PipelineDeal[] }>("/lexoffice/pipeline"),
	});

	const deals = pipelineQuery.data?.data ?? [];

	if (pipelineQuery.isLoading) {
		return (
			<div className="flex justify-center py-12">
				<Loader2 className="h-5 w-5 animate-spin text-stone-400" />
			</div>
		);
	}

	if (deals.length === 0) {
		return (
			<div className="text-center py-12 text-stone-400">
				Keine Vorgänge vorhanden.
			</div>
		);
	}

	return (
		<div className="space-y-3">
			<p className="text-xs text-stone-500">
				{deals.length} Vorgang{deals.length !== 1 ? "e" : ""}
			</p>
			{deals.map((deal) => (
				<DealCard key={deal.groupKey} deal={deal} />
			))}
		</div>
	);
}

// ============================================================
// Deal Card
// ============================================================

function DealCard({ deal }: { deal: PipelineDeal }) {
	const queryClient = useQueryClient();

	const createBookingMutation = useMutation({
		mutationFn: (voucherId: string) =>
			api.post<{
				data: { booking: Record<string, unknown>; linkedVouchers: number };
			}>(`/lexoffice/vouchers/${voucherId}/create-booking`),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["lexoffice"] });
			queryClient.invalidateQueries({ queryKey: ["bookings"] });
		},
	});

	// Pick the best voucher ID for create-booking (prefer invoice > AB > AG)
	const primaryVoucherId =
		deal.steps.invoice?.id ??
		deal.steps.orderconfirmation?.id ??
		deal.steps.quotation?.id;

	const name = deal.booking?.kunde ?? deal.contactName ?? "Unbekannt";
	const hasBooking = !!deal.booking;

	return (
		<div className="card !p-0 overflow-hidden">
			{/* Header */}
			<div className="flex items-center justify-between px-4 py-3">
				<div className="min-w-0 flex-1">
					<div className="flex items-center gap-2">
						<span className="text-sm font-medium text-stone-700 truncate">
							{name}
						</span>
						{deal.totalAmount > 0 && (
							<span className="text-xs text-stone-500 font-mono shrink-0">
								{formatCurrency(deal.totalAmount)}
							</span>
						)}
					</div>
					{deal.latestDate && (
						<p className="text-xs text-stone-400 mt-0.5">
							{formatDate(deal.latestDate)}
						</p>
					)}
				</div>

				{hasBooking ? (
					<span className="badge bg-green-100 text-green-700 text-xs flex items-center gap-1">
						<Check size={10} />
						{deal.booking?.status}
					</span>
				) : primaryVoucherId ? (
					<button
						type="button"
						onClick={() => createBookingMutation.mutate(primaryVoucherId)}
						disabled={createBookingMutation.isPending}
						className="btn-secondary !py-1 !px-2.5 text-xs"
					>
						{createBookingMutation.isPending ? (
							<Loader2 size={12} className="animate-spin" />
						) : (
							<Plus size={12} />
						)}
						Buchung erstellen
					</button>
				) : null}
			</div>

			{/* Pipeline Steps */}
			<div className="border-t border-stone-100 bg-stone-50/50 px-4 py-2.5 flex items-center gap-1">
				<StepIndicator
					label="Angebot"
					step={deal.steps.quotation}
					color="blue"
				/>
				<StepConnector />
				<StepIndicator
					label="AB"
					step={deal.steps.orderconfirmation}
					color="emerald"
				/>
				<StepConnector />
				<StepIndicator
					label="Rechnung"
					step={deal.steps.invoice}
					color="orange"
				/>
			</div>

			{/* Success message */}
			{createBookingMutation.isSuccess && (
				<div className="border-t border-green-100 bg-green-50 px-4 py-2 text-xs text-green-700 flex items-center gap-1.5">
					<Check size={12} />
					Buchung erstellt
					{(createBookingMutation.data?.data?.linkedVouchers ?? 0) > 1 &&
						` (${createBookingMutation.data?.data?.linkedVouchers} Belege verknüpft)`}
				</div>
			)}
		</div>
	);
}

// ============================================================
// Step Indicator
// ============================================================

const STEP_COLORS = {
	blue: {
		active: "bg-blue-100 text-blue-700 border-blue-200",
		dot: "bg-blue-500",
	},
	emerald: {
		active: "bg-emerald-100 text-emerald-700 border-emerald-200",
		dot: "bg-emerald-500",
	},
	orange: {
		active: "bg-orange-100 text-orange-700 border-orange-200",
		dot: "bg-orange-500",
	},
} as const;

function StepIndicator({
	label,
	step,
	color,
}: {
	label: string;
	step: VoucherStep | null;
	color: keyof typeof STEP_COLORS;
}) {
	const colors = STEP_COLORS[color];
	const exists = !!step;
	const isPaid = step?.voucherStatus === "paid";

	if (!exists) {
		return (
			<div className="flex items-center gap-1.5 px-2 py-1 rounded text-xs text-stone-400">
				<span className="w-2 h-2 rounded-full bg-stone-200" />
				{label}
			</div>
		);
	}

	const content = (
		<div
			className={cn(
				"flex items-center gap-1.5 px-2 py-1 rounded border text-xs transition-colors",
				isPaid ? "bg-green-100 text-green-700 border-green-200" : colors.active,
			)}
		>
			<span
				className={cn(
					"w-2 h-2 rounded-full",
					isPaid ? "bg-green-500" : colors.dot,
				)}
			/>
			<span className="font-medium">{label}</span>
			{step.voucherNumber && (
				<span className="font-mono opacity-70">{step.voucherNumber}</span>
			)}
		</div>
	);

	if (step.lexofficeUrl) {
		return (
			<a
				href={step.lexofficeUrl}
				target="_blank"
				rel="noopener noreferrer"
				className="hover:opacity-80 transition-opacity"
				title={`${label} in LexOffice öffnen`}
			>
				{content}
			</a>
		);
	}

	return content;
}

// ============================================================
// Step Connector
// ============================================================

function StepConnector() {
	return <div className="w-4 h-px bg-stone-200 shrink-0" />;
}
